import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical, Pencil } from "lucide-react";
import { ConfidenceBar, ListingLinkButton, ScoreBadge } from "@/components/common/Primitives";
import { BpmOverrideDialog } from "@/components/bpm/BpmOverrideDialog";
import { MarketValueDialog } from "@/components/valuation/MarketValueDialog";
import {
  BpmDialog,
  CostDialog,
  ScoreDialog,
  ValuationDialog,
} from "@/components/explain/ExplainDialogs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MarketStatusBadge, PriceDropCell } from "@/components/opportunities/StatusBadges";
import { RiskDialog } from "@/components/risks/RiskDialog";
import { PriceHistoryDialog } from "@/components/opportunities/PriceHistoryDialog";
import { COLUMNS } from "@/components/opportunities/columns";
import { marketStatusOf } from "@/lib/grouping";
import { useAnalysisDetail } from "@/lib/useAnalysisDetail";
import { countryName, dateOnly, eur, eurSigned, km, pct, vehicleSpec, vehicleSpecShort, vehicleTitle } from "@/lib/format";

const NUMERIC = new Set([
  "registration", "mileage", "price", "bpm", "transport", "import_costs", "landed",
  "market_value", "contribution", "roi",
]);

const isManualBpm = (row) =>
  (row.headline || {}).bpm_source === "MANUAL" || (row.bpm || {}).manual_override === true;

const transportOf = (row) =>
  (row.costs?.lines || []).find((l) => l.key === "transport")?.amount ?? null;

const importCostsOf = (row) => {
  const total = row.costs?.other_costs_excl_purchase;
  if (total === null || total === undefined) return null;
  return total - (transportOf(row) ?? 0) - (row.headline?.bpm ?? 0);
};

const VehicleCell = ({ row }) => {
  const v = row.vehicle_snapshot || {};
  return (
    <div className="flex items-center gap-2">
      {v.image_urls?.[0] ? (
        <img
          src={v.image_urls[0]}
          alt=""
          className="h-9 w-14 shrink-0 rounded-sm border border-slate-200 object-cover"
        />
      ) : (
        <div className="flex h-9 w-14 shrink-0 items-center justify-center rounded-sm border border-dashed border-slate-300 text-[8px] text-slate-400">
          geen foto
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-slate-900">{vehicleTitle(v)}</div>
        <div className="truncate text-[10.5px] text-slate-500">{vehicleSpec(v)}</div>
      </div>
    </div>
  );
};

const BpmCell = ({ row, ui }) => {
  const h = row.headline || {};
  const bpm = row.bpm || {};
  return (
    <div className="flex items-start justify-end gap-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          ui.setDialog({ type: "bpm", row });
        }}
        className="text-right underline decoration-slate-300 decoration-dotted underline-offset-2 transition-colors duration-150 hover:decoration-blue-500"
      >
        {h.bpm === null || h.bpm === undefined ? (
          <>
            <span className="text-[11px] font-semibold text-red-700">BPM ONBEKEND</span>
            <div className="text-[9.5px] uppercase tracking-wide text-red-600">
              CO₂ onvoldoende
            </div>
          </>
        ) : isManualBpm(row) ? (
          <>
            {eur(h.bpm)}
            <div
              className="text-[9.5px] font-bold uppercase tracking-wide text-blue-700"
              data-testid={`bpm-manual-${row.listing_id}`}
            >
              Handmatig{h.bpm_override_scope === "GROUP" ? " · groep" : ""}
            </div>
          </>
        ) : (
          <>
            {h.bpm_has_range && h.bpm_low !== h.bpm_high
              ? `${eur(h.bpm_low)} – ${eur(h.bpm_high)}`
              : eur(h.bpm)}
            <div className="text-[9.5px] uppercase tracking-wide text-slate-500">
              {bpm.status_label}
            </div>
            {bpm.tariff_year && (
              <div
                className="text-[9.5px] tracking-wide text-slate-500"
                data-testid={`bpm-tariff-${row.listing_id}`}
              >
                tarief {bpm.tariff_year}
                {bpm.tariff_saving > 0 && (
                  <span className="ml-1 font-semibold text-emerald-700">
                    −{eur(bpm.tariff_saving)}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </button>
      <button
        type="button"
        title="BPM handmatig aanpassen"
        data-testid={`bpm-edit-${row.listing_id}`}
        onClick={(e) => {
          e.stopPropagation();
          ui.setOverrideRow(row);
        }}
        className="mt-0.5 rounded-sm border border-slate-200 p-0.5 text-slate-400 transition-colors duration-150 hover:border-blue-400 hover:text-blue-600"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  );
};

const MarketValueCell = ({ row, ui }) => {
  const h = row.headline || {};
  return (
    <div className="flex items-start justify-end gap-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          ui.setDialog({ type: "valuation", row });
        }}
        className="text-right underline decoration-slate-300 decoration-dotted underline-offset-2 transition-colors duration-150 hover:decoration-blue-500"
      >
        {h.expected_market_value === null || h.expected_market_value === undefined ? (
          <span className="text-[11px] text-red-700">Onvoldoende vergelijkers</span>
        ) : (
          <>
            {eur(h.expected_market_value)}
            {row.valuation?.value_source === "MANUAL" ? (
              <div
                className="text-[9.5px] font-bold uppercase tracking-wide text-blue-700"
                data-testid={`value-manual-${row.listing_id}`}
              >
                Handmatig
              </div>
            ) : (
              <div
                className={`text-[9.5px] uppercase tracking-wide ${
                  h.valuation_indicative || h.valuation_status === "INDICATIE"
                    ? "font-bold text-red-600"
                    : "text-slate-500"
                }`}
                data-testid={
                  h.valuation_indicative || h.valuation_status === "INDICATIE"
                    ? `value-indicative-${row.listing_id}`
                    : undefined
                }
              >
                {h.valuation_indicative || h.valuation_status === "INDICATIE"
                  ? `Indicatie · ${row.valuation?.n_used ?? 0} vergelijker${
                      (row.valuation?.n_used ?? 0) === 1 ? "" : "s"
                    }`
                  : `${h.confidence_pct ?? row.valuation?.n_used ?? 0}${
                      h.confidence_pct ? "%" : " vergelijkers"
                    }`}
              </div>
            )}
          </>
        )}
      </button>
      <button
        type="button"
        title="Verwacht NL onderbouwen of handmatig aanpassen"
        data-testid={`value-edit-${row.listing_id}`}
        onClick={(e) => {
          e.stopPropagation();
          ui.setValueRow(row);
        }}
        className="mt-0.5 rounded-sm border border-slate-200 p-0.5 text-slate-400 transition-colors duration-150 hover:border-blue-400 hover:text-blue-600"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  );
};

const topFlagLabels = (flags) =>
  (flags.flags || [])
    .filter((f) => f.severity !== "INFO")
    .slice(0, 2)
    .map((f) => f.label)
    .join(" · ") || "—";

const RiskCell = ({ row, ui }) => {
  const flags = row.red_flags || {};
  const counts = flags.counts || {};
  const high = counts.high || 0;
  const medium = counts.medium || 0;
  if (!flags.analysed) {
    return <span className="text-[10.5px] text-slate-400">geen tekst</span>;
  }
  if (!high && !medium) {
    return (
      <button
        type="button"
        data-testid={`risk-cell-${row.listing_id}`}
        onClick={(e) => {
          e.stopPropagation();
          ui.setDialog({ type: "risk", row });
        }}
        className="chip border-emerald-300 bg-emerald-50 text-emerald-800"
      >
        schoon
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid={`risk-cell-${row.listing_id}`}
      onClick={(e) => {
        e.stopPropagation();
        ui.setDialog({ type: "risk", row });
      }}
      className="text-left"
      title="Risico's met bewijs bekijken"
    >
      <span
        className={`chip ${
          high
            ? "border-red-300 bg-red-50 text-red-800"
            : "border-amber-300 bg-amber-50 text-amber-800"
        }`}
      >
        {high ? `${high}× hoog` : `${medium}× middel`}
      </span>
      <div className="mt-0.5 truncate text-[9.5px] uppercase tracking-wide text-slate-500">
        {topFlagLabels(flags)}
      </div>
      {flags.total_penalty > 0 && (
        <div className="text-[9.5px] font-semibold text-red-700">
          −{flags.total_penalty} scorepunten
        </div>
      )}
    </button>
  );
};

const cell = (key, row, ui) => {
  const v = row.vehicle_snapshot || {};
  const h = row.headline || {};
  switch (key) {
    case "vehicle":
      return <VehicleCell row={row} />;
    case "registration":
      return dateOnly(v.first_registration);
    case "mileage":
      return km(v.mileage_km);
    case "country":
      return countryName(v.country);
    case "seller":
      return v.seller_type || "—";
    case "source":
      return <span className="text-[11px] text-slate-500">{v.source}</span>;
    case "price":
      return <span className="font-semibold">{eur(v.price)}</span>;
    case "price_drop":
      return <PriceDropCell market={row.market} testid={`price-drop-${row.listing_id}`} />;
    case "vat":
      return v.vat_status === "Onbekend" ? (
        <span className="text-[11px] text-amber-700">Controle vereist</span>
      ) : (
        <span className="text-[11px]">{v.vat_status}</span>
      );
    case "bpm":
      return <BpmCell row={row} ui={ui} />;
    case "transport":
      return eur(transportOf(row), { fallback: "—" });
    case "import_costs":
      return eur(importCostsOf(row), { fallback: "—" });
    case "landed":
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            ui.setDialog({ type: "cost", row });
          }}
          className="font-semibold underline decoration-slate-300 decoration-dotted underline-offset-2 hover:decoration-blue-500"
        >
          {eur(h.landed_cost)}
        </button>
      );
    case "market_value":
      return <MarketValueCell row={row} ui={ui} />;
    case "contribution":
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            ui.setDialog({ type: "cost", row });
          }}
          className={`font-bold underline decoration-slate-300 decoration-dotted underline-offset-2 hover:decoration-blue-500 ${
            (h.contribution ?? 0) > 0 ? "text-emerald-700" : "text-red-700"
          }`}
        >
          {eurSigned(h.contribution, { fallback: "—" })}
        </button>
      );
    case "roi":
      return <span className="font-semibold">{pct(h.roi_pct, { fallback: "—" })}</span>;
    case "score":
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            ui.setDialog({ type: "score", row });
          }}
          className="text-left"
        >
          <ScoreBadge score={h.deal_score} advice={h.advice} />
        </button>
      );
    case "confidence":
      return <ConfidenceBar value={h.confidence_pct} label={null} />;
    case "risk":
      return <RiskCell row={row} ui={ui} />;
    case "status":
      return (
        <MarketStatusBadge
          status={marketStatusOf(row)}
          testid={`market-status-${row.listing_id}`}
        />
      );
    default:
      return null;
  }
};

export const OpportunityRows = ({ rows, columns, onChanged, testid = "opportunity-rows" }) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({ type: null, row: null });
  const [overrideRow, setOverrideRow] = useState(null);
  const [valueRow, setValueRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const ui = { setDialog, setOverrideRow, setValueRow, setHistoryRow };
  const visible = COLUMNS.filter((c) => columns.includes(c.key));
  const active = dialog.row;

  const detailListingId = valueRow?.listing_id ?? active?.listing_id ?? null;
  const detailEnabled =
    valueRow !== null || ["bpm", "valuation", "score", "cost"].includes(dialog.type);
  const { detail, loading, patch } = useAnalysisDetail(detailListingId, detailEnabled);
  const detailFor = (row) =>
    detail && row && detail.listing_id === row.listing_id ? detail : null;
  const dialogChanged = (data) => {
    if (data) patch(data);
    onChanged?.(data);
  };

  const kebab = (row) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`row-menu-${row.listing_id}`}
          onClick={(e) => e.stopPropagation()}
          className="rounded-sm border border-slate-200 p-1 text-slate-500 transition-colors duration-150 hover:border-blue-400 hover:text-blue-700"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 text-[12.5px]">
        <DropdownMenuItem onClick={() => navigate(`/auto/${row.listing_id}`)}>
          Details openen
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => row.vehicle_snapshot?.url && window.open(row.vehicle_snapshot.url, "_blank")}
        >
          Originele advertentie
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setOverrideRow(row)}>BPM aanpassen</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setValueRow(row)}>Verwacht NL aanpassen</DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setHistoryRow(row)}
          data-testid={`menu-price-history-${row.listing_id}`}
        >
          Prijshistorie
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <div className="hidden overflow-x-auto md:block" data-testid={testid}>
        <table className="data-table">
          <thead>
            <tr>
              {visible.map((c) => (
                <th key={c.key} className={NUMERIC.has(c.key) ? "text-right" : ""}>
                  {c.label}
                </th>
              ))}
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer"
                data-testid={`row-${row.listing_id}`}
                onClick={() => navigate(`/auto/${row.listing_id}`)}
              >
                {visible.map((c) => (
                  <td
                    key={c.key}
                    className={`${NUMERIC.has(c.key) ? "num text-right" : "text-[12px]"} ${
                      c.key === "vehicle" ? "max-w-[250px]" : ""
                    }`}
                    data-testid={`cell-${c.key}-${row.listing_id}`}
                  >
                    {cell(c.key, row, ui)}
                  </td>
                ))}
                <td>{kebab(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 p-2 md:hidden" data-testid={`${testid}-mobile`}>
        {rows.map((row) => {
          const v = row.vehicle_snapshot || {};
          const h = row.headline || {};
          return (
            <div key={row.id} className="panel p-2.5" data-testid={`m-row-${row.listing_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-slate-900">
                    {vehicleTitle(v)}
                  </div>
                  <div className="text-[11px] text-slate-500">{vehicleSpecShort(v)}</div>
                  <div className="mt-1">
                    <MarketStatusBadge status={marketStatusOf(row)} />
                  </div>
                </div>
                <ScoreBadge score={h.deal_score} advice={h.advice} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-slate-100 pt-2">
                {[
                  ["Inkoop", eur(v.price)],
                  [
                    isManualBpm(row) ? "BPM (handmatig)" : "BPM",
                    h.bpm === null || h.bpm === undefined ? "BPM onbekend" : eur(h.bpm),
                  ],
                  ["Landed cost", eur(h.landed_cost)],
                  ["Verwacht NL", eur(h.expected_market_value)],
                  ["Bijdrage", eurSigned(h.contribution, { fallback: "—" })],
                  ["ROI", pct(h.roi_pct, { fallback: "—" })],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-2">
                    <span className="label-xs">{label}</span>
                    <span className="mono text-[12px] font-semibold text-slate-800">{value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/auto/${row.listing_id}`)}
                  data-testid={`m-details-${row.listing_id}`}
                  className="rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={() => setOverrideRow(row)}
                  data-testid={`m-bpm-edit-${row.listing_id}`}
                  className="flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                >
                  <Pencil className="h-3 w-3" /> BPM
                </button>
                <ListingLinkButton url={v.url} testid={`m-listing-url-${row.listing_id}`} />
              </div>
            </div>
          );
        })}
      </div>

      <PriceHistoryDialog
        open={historyRow !== null}
        onOpenChange={(open) => !open && setHistoryRow(null)}
        row={historyRow}
      />
      <MarketValueDialog
        open={valueRow !== null}
        onOpenChange={(open) => !open && setValueRow(null)}
        loading={loading && valueRow !== null}
        row={
          valueRow
            ? {
                ...valueRow,
                ...(detail && detail.listing_id === valueRow.listing_id ? detail : {}),
              }
            : null
        }
        onApplied={(data) => {
          setValueRow(null);
          dialogChanged(data);
        }}
      />
      <BpmOverrideDialog
        open={overrideRow !== null}
        onOpenChange={(open) => !open && setOverrideRow(null)}
        row={overrideRow}
        onApplied={() => {
          setOverrideRow(null);
          onChanged?.();
        }}
      />
      <BpmDialog
        open={dialog.type === "bpm"}
        onOpenChange={() => setDialog({ type: null, row: null })}
        bpm={detailFor(active)?.bpm}
        co2={detailFor(active)?.co2}
        listingId={active?.listing_id}
        onChanged={dialogChanged}
      />
      <RiskDialog
        open={dialog.type === "risk"}
        onOpenChange={() => setDialog({ type: null, row: null })}
        flags={active?.red_flags}
      />
      <ValuationDialog
        open={dialog.type === "valuation"}
        onOpenChange={() => setDialog({ type: null, row: null })}
        valuation={detailFor(active)?.valuation}
        listingId={active?.listing_id}
        onChanged={dialogChanged}
      />
      <ScoreDialog
        open={dialog.type === "score"}
        onOpenChange={() => setDialog({ type: null, row: null })}
        score={detailFor(active)?.deal_score}
      />
      <CostDialog
        open={dialog.type === "cost"}
        onOpenChange={() => setDialog({ type: null, row: null })}
        costs={detailFor(active)?.costs}
        scenario={detailFor(active)?.scenarios?.base}
      />
    </>
  );
};
